import React from 'react';
import Dropzone from 'react-dropzone';
import { PropTypes } from 'prop-types';

import api from 'api';
import { Button } from '@ontruck/orc';
import { fileToObj, readFile } from 'utils/files';

import LoadSpinner from 'components/LoadSpinner';

import PDFItem from './pdf-item';

const gatHasSomeNewFile = ({ originalFiles, currentFiles }) => {
  const originalIds = (originalFiles || []).map((file) => file.id);
  const currentIds = Object.keys(currentFiles || {});

  return (
    originalIds.some((id) => currentIds.indexOf(id) < 0) ||
    currentIds.some((id) => originalIds.indexOf(id) < 0)
  );
};

class AdditionalDocumentsDropZone extends React.Component {
  constructor(props) {
    super(props);

    const { files } = this.props;

    this.state = {
      error: false,
      initialFiles: files,
      filesById: (files || []).reduce(
        (acc, file) => ({ ...acc, [file.id]: { ...file } }),
        {}
      ),
      loading: false,
      submitted: false,
    };

    this.onDrop = this.onDrop.bind(this);
    this.uploadFile = this.uploadFile.bind(this);
    this.onDeleteFile = this.onDeleteFile.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
  }

  onDeleteFile(file) {
    this.setState((prevState) => {
      const newFilesById = prevState.filesById;
      delete newFilesById[file.id];

      return {
        filesById: newFilesById,
        submitted: false,
        error: false,
      };
    });
  }

  uploadFile = async (file) => {
    const { pathToUpload } = this.props;
    let res = false;
    const fileReaded = await readFile(file);
    const fileObj = fileToObj(file);

    try {
      const response = await api.post(`${pathToUpload}`, {
        content: fileReaded,
        file_name: file.name,
      });
      const { id } = (await response) && response.data;
      res = { ...fileObj, id };
      return res;
    } catch (e) {
      this.setState({
        error: true,
      });

      return {};
    }
  };

  onDrop = async (acceptedFiles) => {
    const { canUploadFiles } = this.props;
    if (!canUploadFiles) return;

    this.setState({ loading: true, submitted: false, error: false });
    try {
      const responses = await Promise.all(
        acceptedFiles.map((file) => this.uploadFile(file))
      );
      this.setState((prevState) => ({
        filesById: {
          ...prevState.filesById,
          ...(responses || []).reduce(
            (acc, file) =>
              file && file.id ? { ...acc, [file.id]: file } : { ...acc },
            {}
          ),
        },
      }));
    } catch (e) {
      console.log(e);
    } finally {
      this.setState({ loading: false });
    }
  };

  handleSubmit = () => {
    const { onSubmit } = this.props;
    const { filesById } = this.state;

    onSubmit({ files: Object.keys(filesById || {}) || [] });
    this.setState({
      initialFiles: Object.values(filesById || {}) || [],
      submitted: true,
      error: false,
    });
  };

  handleCancel = () => {
    const { onCancel } = this.props;
    const { filesById, initialFiles } = this.state;

    const hasSomeNewFile = gatHasSomeNewFile({
      originalFiles: initialFiles,
      currentFiles: filesById,
    });

    if (hasSomeNewFile) {
      this.setState({
        filesById: (initialFiles || []).reduce(
          (acc, file) => ({ ...acc, [file.id]: { ...file } }),
          {}
        ),
      });
    } else {
      onCancel();
    }
  };

  render() {
    const {
      className,
      t,
      title,
      onSubmit,
      onCancel,
      isEditing,
      canUploadFiles,
    } = this.props;

    const { error, filesById, loading, initialFiles, submitted } = this.state;

    const hasSubmitAction = !!onSubmit;
    const hasCancelAction = !!onCancel || isEditing;

    const hasActions = hasSubmitAction || hasCancelAction;
    const hasSomeNewFile = gatHasSomeNewFile({
      originalFiles: initialFiles,
      currentFiles: filesById,
    });

    const files = Object.values(filesById || {}) || [];

    return (
      <div className={className}>
        {title && <span className="title">{title}</span>}

        <Dropzone
          accept="application/pdf"
          activeClassName="active-dropzone"
          rejectClassName="reject-dropzone"
          onDrop={this.onDrop}
        >
          {({ getRootProps, getInputProps, isDragActive }) => (
            <div
              {...getRootProps()}
              className={` dropzone ${
                hasSomeNewFile || isDragActive ? 'active-dropzone' : ''
              } ${error ? 'error-dropzone' : ''} `}
            >
              {canUploadFiles && <input {...getInputProps()} />}
              <>
                {files && files.length > 0 && (
                  <div className="files-container">
                    {(files || []).map((file) => (
                      <PDFItem
                        key={file.id}
                        onDelete={this.onDeleteFile}
                        file={file}
                      />
                    ))}
                  </div>
                )}

                {canUploadFiles && (
                  <>
                    {files.length > 0 ? (
                      <Button nude>{t('add_more_doc')}</Button>
                    ) : (
                      <div>
                        <span className="dropzone-help">{t('drop')}</span>
                        <Button nude className="dropzone-help-ctive">
                          {t('select_from')}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </>
            </div>
          )}
        </Dropzone>

        {error && (
          <div className="dropzoneError">
            <span>{t('error_uploading')}</span>
          </div>
        )}

        <div>
          <span>{t('driver_print')}</span>
        </div>

        {loading && <LoadSpinner skin="standar" size={4} />}
        {((hasActions && hasSomeNewFile) || isEditing) && !submitted && (
          <div className="actions">
            {hasSubmitAction && hasSomeNewFile && (
              <Button onClick={this.handleSubmit}>{t('save')}</Button>
            )}
            {hasCancelAction && (
              <Button skin="secondary" onClick={this.handleCancel}>
                {hasSomeNewFile ? t('discard') : t('close')}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }
}

AdditionalDocumentsDropZone.propTypes = {
  className: PropTypes.string,
  t: PropTypes.func,

  title: PropTypes.string,

  files: PropTypes.arrayOf(PropTypes.shape({})),
  pathToUpload: PropTypes.string,
  onFileUploadSucess: PropTypes.func,
  onFileUploadError: PropTypes.func,

  options: PropTypes.arrayOf(PropTypes.shape({})),
  optionSelected: PropTypes.string,
  onOptionSelected: PropTypes.func,

  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,

  isEditing: PropTypes.bool,
  canUploadFiles: PropTypes.bool,
};

AdditionalDocumentsDropZone.defaultProps = {
  className: '',
  t: () => ({}),

  title: '',

  files: [],
  pathToUpload: 'additional_documents/',
  onFileUploadSucess: () => ({}),
  onFileUploadError: () => ({}),

  options: [],
  optionSelected: undefined,
  onOptionSelected: () => ({}),

  onSubmit: () => ({}),
  onCancel: () => ({}),

  isEditing: false,
  canUploadFiles: false,
};

export default AdditionalDocumentsDropZone;
